import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack, Section } from 'tio-ui/components/layout';
import { ProgressBar } from 'tio-ui/components/status';
import { t } from 'ember-intl';
import type FeaturesService from 'tio-common/services/features';
import type IntlService from 'ember-intl/services/intl';
import type AccountModel from 'tio-common/models/account';

interface QualifyingPaymentTrackerSignature {
  Args: {
    accounts?: AccountModel[];
  };
  Element: HTMLDivElement;
}

class QualifyingPaymentTracker extends Component<QualifyingPaymentTrackerSignature> {
  @service declare features: FeaturesService;
  @service declare intl: IntlService;

  readonly TOTAL_REQUIRED_PAYMENTS = 120;

  get isEnabled() {
    return this.features.isEnabled('pslfQualifyingPaymentTracker');
  }

  get accountsSummary() {
    const accounts = this.args.accounts ?? [];
    let maxQualifyingPayments = 0;
    let latestProviderDate: Date | undefined;

    for (const account of accounts) {
      // Track latest provider success date
      if (account.providerSuccessAt) {
        const providerDate = new Date(account.providerSuccessAt);
        if (!latestProviderDate || providerDate > latestProviderDate) {
          latestProviderDate = providerDate;
        }
      }

      // Get max qualifying payments across all loans
      for (const loan of account.loans ?? []) {
        const qualified = loan.observation?.['forgiveness-program-qualified'] ?? 0;
        maxQualifyingPayments = Math.max(maxQualifyingPayments, qualified);
      }
    }

    return {
      maxQualifyingPayments,
      latestProviderDate,
    };
  }

  get progressPercentage() {
    return Math.min(
      Math.round((this.accountsSummary.maxQualifyingPayments / this.TOTAL_REQUIRED_PAYMENTS) * 100),
      100
    );
  }

  get formattedLastUpdated() {
    const date = this.accountsSummary.latestProviderDate;
    if (!date) {
      return '';
    }
    return this.intl.formatDate(date, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  <template>
    {{#if this.isEnabled}}
      <Section>
        <:header>{{t "pslf.payment_tracker.title"}}</:header>
        <:subHeader>{{t "pslf.payment_tracker.description"}}</:subHeader>
        <:body>
          <VStack>
            <div class="text-4xl font-bold text-gray-900">
              {{this.progressPercentage}}%
            </div>

            <ProgressBar
              @progress={{this.progressPercentage}}
              @label="PSLF Progress"
              @intent="primary"
            />

            <div class="flex justify-between items-center text-sm text-gray-600">
              <span>
                {{t "pslf.payment_tracker.last_updated" date=this.formattedLastUpdated}}
              </span>
              <span class="font-medium">
                {{this.accountsSummary.maxQualifyingPayments}}/{{this.TOTAL_REQUIRED_PAYMENTS}}
                {{t "pslf.payment_tracker.payments"}}
              </span>
            </div>
          </VStack>
        </:body>
      </Section>
    {{/if}}
  </template>
}

export default QualifyingPaymentTracker;
